<template>
  <div class="px-4 mb-3">
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="mb-6">
        <router-link to="/ecosystem" class="inline-flex items-center text-orange-500 font-medium hover:text-orange-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Requests
        </router-link>
      </div>

      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-900">Blood Request Details</h1>
        <div v-if="facility_request" class="inline-flex items-center px-3 py-1 rounded-md" :class="{
          'bg-green-200 text-green-800': facility_request.status === 'Completed',
          'bg-yellow-200 text-yellow-800': facility_request.status === 'Pending',
          'bg-blue-200 text-blue-800': facility_request.status === 'Partially Issued',
          'bg-red-200 text-red-800': facility_request.status === 'Rejected',
          'bg-gray-200 text-gray-800': ![
            'Completed',
            'Pending',
            'Partially Issued',
            'Rejected',
          ].includes(facility_request.status),
        }">
          {{ facility_request.status }}
        </div>
      </div>

      <div v-if="error" class="bg-red-100 text-red-700 p-4 rounded-md mb-4">
        {{ error }}
      </div>
      <div v-else-if="!facility_request" class="bg-gray-100 text-gray-700 p-4 rounded-md mb-4">
        Loading requisition details...
      </div>
      <div v-else>
        <!-- Request Information Card -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-800">Request Information</h2>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p class="text-sm font-medium text-gray-500">Requesting Facility</p>
                <p class="text-base font-medium text-gray-900">{{ facility_request.requesting_facility?.name || 'N/A' }}
                </p>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-500">Date Requested</p>
                <p class="text-base font-medium text-gray-900">{{ formatDateISO(facility_request.created_at) }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-500">Facility Contact</p>
                <p class="text-base font-medium text-gray-900">{{
                  facility_request.requesting_facility?.contact_person_phone || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-500">Date Required</p>
                <p class="text-base font-medium text-gray-900">{{ formatDateISO(facility_request.date_required) }}</p>
              </div>
              <div class="md:col-span-2">
                <p class="text-sm font-medium text-gray-500">Total Quantity Requested</p>
                <p class="text-base font-medium text-gray-900">{{ calculateTotalQuantity() }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Requested Components Table -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-800">Requested Components</h2>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-left">
              <thead class="bg-gray-50 text-gray-600 text-sm">
                <tr>
                  <th class="px-6 py-3 font-medium">Component</th>
                  <th class="px-6 py-3 font-medium">Quantity Requested</th>
                  <th class="px-6 py-3 font-medium">Quantity Issued</th>
                  <th class="px-6 py-3 font-medium">Blood Group</th>
                  <th class="px-6 py-3 font-medium">Status</th>
                  <th class="px-6 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="(product, index) in facility_request.ecosystem_products" :key="index"
                  class="hover:bg-gray-50">
                  <td class="px-6 py-4">{{ product.unit_product_name || 'N/A' }}</td>
                  <td class="px-6 py-4">{{ formatWholeNumber(product.requested_quantity) }}</td>
                  <td class="px-6 py-4">{{ formatWholeNumber(product.issued_quantity) }}</td>
                  <td class="px-6 py-4">
                    <span
                      class="inline-flex justify-center px-2.5 py-1 rounded-md bg-red-100 text-red-800">
                      {{ product.blood_group_name || 'N/A' }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <span class="inline-flex justify-center px-2.5 py-1 w-full rounded-md" :class="{
                      'bg-green-200 text-green-800': product.status === 'Completed',
                      'bg-yellow-200 text-yellow-800': product.status === 'Pending',
                      'bg-blue-200 text-blue-800': product.status === 'Partially Issued',
                      'bg-red-200 text-red-800': product.status === 'Rejected',
                      'bg-gray-200 text-gray-800': ![
                        'Completed',
                        'Pending',
                        'Partially Issued',
                        'Rejected',
                      ].includes(product.status),
                    }">
                      {{ product.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4">
                    <button v-if="product.status != 'Completed'"
                      :disabled="current_facility_id && facility_request?.request_to && current_facility_id === facility_request.requesting_facility.facility_id && (facility_code == 'KTTA' && facility_request.request_to_facility.facility_code != 'KTTA')"
                      @click="handleOpenIssueModal(product)" :class="{
                        'px-4 py-2 text-sm font-medium rounded text-white': true,
                        'bg-orange-500 hover:bg-orange-600': !(
                          current_facility_id &&
                          facility_request?.request_to && current_facility_id === facility_request.requesting_facility.facility_id && (facility_code == 'KTTA' && facility_request.request_to_facility.facility_code != 'KTTA')
                        ),
                        'bg-gray-400 cursor-not-allowed': current_facility_id &&
                          facility_request?.request_to && current_facility_id === facility_request.requesting_facility.facility_id && (facility_code == 'KTTA' && facility_request.request_to_facility.facility_code != 'KTTA')
                      }">
                      Issue Out
                    </button>
                    <button v-if="product.status === 'Completed'" @click="handleOpeViewDetailsModal(product)"
                      class="px-4 py-2 text-sm font-medium rounded bg-blue-800 hover:bg-blue-900 text-white">
                      View Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!--already issued unit log modal -->
      <div v-if="showDetailsModal"
        class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm"
        @click.self="closeIssueModal">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
          <!-- Modal header -->
          <div class="bg-orange-200 text-gray-600 px-6 py-2 flex justify-between items-center">
            <h2 class="text-xl font-bold flex items-center">
              Issued Out Blood Unit Details
            </h2>
            <button @click="closeDetailsModal"
              class="text-gray-700 hover:bg-custom-blue-dark p-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Modal content -->
          <div id="modalContent" class="p-6 overflow-y-auto" style="max-height: calc(85vh - 128px);">
            <div v-if="selectedProduct" class="space-y-6">
              <div class="bg-white rounded-lg p-4 border border-gray-200">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                  <div>
                    <p class="text-base text-gray-500 mb-1">Requesting Facility</p>
                  </div>
                  <div>
                    <p class="text-base font-medium text-gray-900">{{ selectedProduct.facility_issued_to?.name || '' }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Unit History -->
              <div class="mt-8">
                <h2 class="text-xl font-bold text-gray-800 mb-4">Unit History</h2>
                <div class="relative overflow-x-auto mt-2 rounded-lg shadow-sm border border-gray-200">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50">
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Pint Serial</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Blood Group</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Blood Product</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Expiry Date</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Issue Date</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Received Date</th>
                        <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in selectedProduct.products.flatMap(product => product.logs.map(log => ({ product, log })))"
                        :key="`${item.product.product_id}-${item.log.ecosystem_issueout_log_id}`"
                        class="hover:bg-gray-100 transition-colors border-b">
                        <td class="py-4 px-4 text-sm text-gray-900">{{ item.product.pint_serial || 'N/A' }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900">{{ item.product.blood_group?.description || 'N/A' }}
                        </td>
                        <td class="py-4 px-4 text-sm text-gray-900">{{ item.product.unit_product?.name || 'N/A' }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900">{{ formatDateISO(item.product.expiry_date) }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900">{{ formatDateISO(item.log.issue_date) }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900">{{ formatDateISO(item.log.received_date) }}</td>
                        <td class="py-4 px-4 text-sm text-gray-900">
                          <span
                            class="inline-flex justify-center px-2.5 py-1 w-full rounded-md bg-blue-100 text-blue-800">
                            {{ item.log.status || 'N/A' }}
                          </span>
                        </td>
                      </tr>
                      <tr
                        v-if="!selectedProduct.products || selectedProduct.products.every(product => !product.logs || product.logs.length === 0)"
                        class="border-b">
                        <td colspan="15" class="py-4 px-4 text-sm text-gray-500 text-center">No logs available.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <!-- Footer buttons -->
          <div class="flex justify-between gap-3 mx-6 py-4 border-t border-gray-300">
            <button @click="closeDetailsModal"
              class="bg-gray-200 text-gray-800 text-sm py-2 px-6 rounded-md hover:bg-custom-blue-dark flex items-center">
              Cancel
            </button>
            <button @click="printIssueout(selectedProduct)"
              class="bg-custom-blue text-white text-sm py-2 px-6 rounded-md hover:bg-custom-blue-dark flex items-center">
              Print
            </button>
          </div>
        </div>
      </div>

      <!--Issueout Modal -->
      <div v-if="showModal"
        class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm"
        @click.self="closeIssueModal">
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] overflow-hidden">
          <!-- Modal header -->
          <div class="bg-orange-200 text-gray-600 px-6 py-2 flex justify-between items-center">
            <h2 class="text-xl font-bold flex items-center">Issue Out Blood Unit</h2>
            <button @click="closeIssueModal"
              class="text-gray-800 hover:bg-custom-blue-dark p-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!--Modal content -->
          <div id="modalContent" class="p-6 overflow-y-auto max-h-[79vh]">
            <!-- Current blood unit info -->
            <div class="bg-blue-50 p-4 rounded-md mb-6 grid grid-cols-2 gap-4">
              <div>
                <p class="text-sm text-blue-700 font-medium">Component:</p>
                <p class="text-blue-900 font-medium">{{ selectedProduct?.unit_product_name || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-sm text-blue-700 font-medium">Requested Quantity:</p>
                <p class="text-blue-900 font-medium">{{ selectedProduct?.requested_quantity || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-sm text-blue-700 font-medium">Blood Group:</p>
                <p class="text-blue-900 font-medium">{{ selectedProduct?.blood_group_name || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-sm text-blue-700 font-medium">Pending Quantity:</p>
                <p class="text-blue-900 font-medium">{{ getPendingQuantity(selectedProduct) }}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <!-- Form fields -->
              <div class="space-y-4">
                <div>
                  <label class="block text-gray-700 mb-2">Issue to (Facility Name) <span
                      class="text-red-500">*</span></label>
                  <input type="text" v-model="facilityName"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter facility name" required />
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">Blood Group <span class="text-red-500">*</span></label>
                  <div class="relative">
                    <select v-model="formData.blood_group_id"
                      class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      @change="filterUnitProducts" required>
                      <option value="" disabled>Select blood group</option>
                      <option v-for="group in bloodGroups" :key="group.blood_group_id" :value="group.blood_group_id">
                        {{ group.name }}
                      </option>
                    </select>
                    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">Pint Serial(s) <span class="text-red-500">*</span></label>
                  <div class="relative">
                    <div class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer"
                      @click="toggleDropdown">
                      <div class="flex flex-wrap gap-2">
                        <span v-for="(productId, index) in formData.product_ids" :key="productId"
                          class="bg-blue-100 text-blue-600 px-2 py-1 rounded">
                          {{ getProductName(productId) }}
                          <span class="cursor-pointer ml-1 text-red-500" @click.stop="removeProduct(index)">×</span>
                        </span>
                      </div>
                      <div v-if="!formData.product_ids.length" class="text-gray-400">
                        Select unit serial number
                      </div>
                    </div>
                    <div v-if="isDropdownVisible" class="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                      <ul class="max-h-60 overflow-y-auto">
                        <li v-for="product in filteredUnitProducts" :key="product.product_id"
                            class="px-4 py-2 cursor-pointer hover:bg-gray-100" @click="selectProduct(product)">
                          <div class="flex items-center space-x-4">
                            <span>{{ product.pint_serial }}</span>
                            <span>({{ product.blood_group?.description }})</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">Issued By <span class="text-red-500">*</span></label>
                  <select v-model="formData.issued_by"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required>
                    <option value="" disabled>Select User</option>
                    <option v-for="user in users" :key="user.user_id" :value="user.user_id">
                      {{ user.first_name }} {{ user.last_name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">Issue Date <span class="text-red-500">*</span></label>
                  <input type="date" v-model="formData.issued_date"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required />
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">Received By <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.received_by"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter name" required />
                </div>
                <div>
                  <label class="block text-gray-700 mb-2">Received Date <span class="text-red-500">*</span></label>
                  <input type="date" v-model="formData.received_date"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required />
                </div>
              </div>
              <!-- Issued units table -->
              <div class="mb-6 overflow-x-auto">
                <table class="min-w-full bg-white border-t border-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="py-3 px-4 text-left text-sm font-medium text-gray-700">Pint Serial</th>
                      <th class="py-3 px-4 text-left text-sm font-medium text-gray-700">Product</th>
                      <th class="py-3 px-4 text-left text-sm font-medium text-gray-700">Blood Group</th>
                      <th class="py-3 px-4 text-left text-sm font-medium text-gray-700">Quantity</th>
                      <th class="py-3 px-4 text-left text-sm font-medium text-gray-700">Expiry Date</th>
                      <th class="py-3 px-4 text-left text-sm font-medium text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr v-for="(unit, index) in selectedProducts" :key="unit.product_id"
                      class="border-t border-gray-200">
                      <td class="py-3 px-4 text-sm text-gray-700">{{ unit.pint_serial }}</td>
                      <td class="py-3 px-4 text-sm text-gray-700">{{ unit.unit_product?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-sm text-gray-700">{{ unit.blood_group?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-sm text-gray-700">{{ unit.quantity || 'N/A' }}</td>
                      <td class="py-3 px-4 text-sm text-gray-700">
                        {{ unit.expiry_date ? new Date(unit.expiry_date).toLocaleDateString('en-CA') : 'N/A' }}
                      </td>
                      <td class="py-3 px-4 text-center">
                        <button @click="removeProduct(index)" class="text-gray-700 hover:text-red-600">
                          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clip-rule="evenodd"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
          <!-- Footer buttons -->
          <div class="flex justify-between gap-3 m-6 py-4 border-t border-gray-300">
            <button @click="closeIssueModal"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Cancel
            </button>
            <button @click="submitIssueForm"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
              Issue Out
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/services/api";
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: "FacilityRequisitionDetailsPage",
  components: {},
  data() {
    return {
      facility_request: null,
      error: null,
      loading: false,
      userData: null,
      facility_logo: null,
      facility_code: null,
      current_facility_id: null,
      showModal: false,
      showDetailsModal: false,
      selectedProduct: null,
      selectedComponent: null,
      bloodGroups: [],
      unitProducts: [],
      filteredUnitProducts: [],
      selectedProducts: [],
      users: [],
      isDropdownVisible: false,
      formData: {
        ecosystem_request_id: '',
        ecosystem_product_id: '',
        facility_issued_to: '',
        facility_issuing: '',
        blood_group_id: '',
        product_ids: [],
        issued_by: '',
        issued_date: '',
        received_by: '',
        received_date: '',
      },
    };
  },
  computed: {
    facilityName: {
      get() {
        return this.facility_request?.requesting_facility?.name || '';
      },
      set(value) {
        this.formData.facility_issued_to = value; // Update formData.facility_issued_to when user edits
      },
    },
  },
  mounted() {
    this.userData = this.$store.getters["auth/user"];
    this.facility_code = this.userData?.user_roles[0]?.facility_department?.facility?.facility_code || '';
    this.current_facility_id = this.userData?.user_roles[0]?.facility_department?.facility?.facility_id || '';
    this.facility_logo = this.userData?.user_roles[0]?.facility_department?.facility?.logo || '';
    this.formData.facility_issuing = this.userData?.user_roles[0]?.facility_department?.facility?.facility_id || '';
    this.fetchBloodGroups();
    this.fetchUnitProducts();
    this.getUsers();
  },
  created() {
    const request_id = this.$route.params.ecosystem_request_id;
    console.log('Requisition ID:', request_id); // Debug
    if (request_id) {
      this.getEcosystemRequisition(request_id);
    } else {
      this.error = "No requisition ID provided";
    }
  },
  methods: {
    async getEcosystemRequisition(requisition_id) {
      this.loading = true;
      try {
        const response = await api.getEcosystemBloodRequest(requisition_id);
        console.log('API Response:', response); // Debug
        this.facility_request = response.data.data || response.data || response;
      } catch (error) {
        console.error('API Error:', error.response || error.message); // Debug
        this.error = error.response?.data?.errors || error.message || "Failed to fetch requisition details";
      } finally {
        this.loading = false;
      }
    },
    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        console.log('Blood Groups:', response); // Debug
        this.bloodGroups = response.data || response;
      } catch (error) {
        console.error("Error fetching blood groups:", error);
        this.error = "Failed to fetch blood groups";
      }
    },
    async fetchUnitProducts() {
      try {
        const response = await api.fetchAllProducts();
        console.log('Unit Products:', response); // Debug
        this.unitProducts = (response.data || response).filter(product => product.status === "Okay");
      } catch (error) {
        console.error("Error fetching unit products:", error);
        this.error = "Failed to fetch unit products";
      }
    },
    async getUsers() {
      try {
        const response = await api.getUsers();
        console.log('Users:', response); // Debug
        this.users = (response.data || response).filter(
          user => Array.isArray(user.user_roles) && user.user_roles.length > 0
        );
      } catch (error) {
        console.error("Error fetching users:", error);
        this.error = "Failed to fetch users";
      }
    },
    async getEcosystemUnitsLogs(ecosystem_product_id) {
      this.loading = true;
      try {
        const response = await api.fetchEcosystemUnitsJourneyLogs(ecosystem_product_id);
        this.selectedProduct = response.data || response;
      } catch (error) {
        this.error = error.message || "Failed to fetch facility requests";
      } finally {
        this.loading = false;
      }
    },
    filterUnitProducts() {
      this.filteredUnitProducts = this.unitProducts ?? [];

      //// if ever you are doing compatibility matching

      // if (this.formData.blood_group_id) {
      //   this.filteredUnitProducts = this.unitProducts.filter(
      //     product => product.blood_group_id === this.formData.blood_group_id &&
      //                product.unit_product_id === this.selectedProduct?.unit_product_id
      //   );
      // } else {
      //   this.filteredUnitProducts = [];
      // }
    },
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    getProductName(productId) {
      const product = this.filteredUnitProducts.find(p => p.product_id === productId);
      return product ? product.pint_serial : '';
    },
    selectProduct(product) {
      if (!this.selectedProducts.some(p => p.product_id === product.product_id)) {
        this.selectedProducts.push(product);
        this.formData.product_ids = this.selectedProducts.map(p => p.product_id);
      }
      this.isDropdownVisible = false;
    },
    removeProduct(index) {
      this.selectedProducts.splice(index, 1);
      this.formData.product_ids = this.selectedProducts.map(p => p.product_id);
    },
    handleOpenIssueModal(product) {
      this.selectedProduct = product;
      this.formData.ecosystem_request_id = this.facility_request?.ecosystem_request_id || '';
      this.formData.ecosystem_product_id = product.ecosystem_product_id || '';
      this.formData.blood_group_id = product.blood_group_id;
      this.formData.facility_issued_to = this.facility_request?.requesting_facility || '';
      this.filterUnitProducts();
      this.showModal = true;
    },
    handleOpeViewDetailsModal(item) {
      this.showDetailsModal = true;
      this.selectedComponent = item
      this.getEcosystemUnitsLogs(item.ecosystem_product_id);
    },
    closeIssueModal() {
      this.showModal = false;
      this.selectedProduct = null;
      this.selectedProducts = [];
      this.formData = {
        ecosystem_request_id: '',
        ecosystem_product_id: '',
        facility_issued_to: '',
        facility_issuing: this.userData?.user_roles[0]?.facility_department?.facility?.facility_id || '',
        blood_group_id: '',
        product_ids: [],
        issued_by: '',
        issued_date: '',
        received_by: '',
        received_date: '',
      };
      this.isDropdownVisible = false;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedProduct = null;
    },
    validateFields() {
      const emptyFields = [];
      if (!this.formData.product_ids) emptyFields.push('Component');
      if (!this.formData.issued_by) emptyFields.push('Issued By');
      if (!this.formData.issued_date) emptyFields.push('Issue Date');
      if (!this.formData.received_by) emptyFields.push('Received By');
      if (!this.formData.received_date) emptyFields.push('Received Date');

      return emptyFields;
    },
    async submitIssueForm() {
      const emptyFields = this.validateFields();
      if (emptyFields.length > 0) {
        SweetAlertService.showEmptyFieldsAlert(emptyFields);
        return;
      }

      console.log('Form Data:', this.formData); // Debug

      try {
        await api.createEcosystemBloodIssueout(this.formData);
        this.closeIssueModal();
        this.getEcosystemRequisition(this.$route.params.ecosystem_request_id);
      } catch (error) {
        this.error = error.response?.data?.errors || error.message || "Failed to issue unit";
      }
    },
    formatDateISO(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    },
    formatWholeNumber(value) {
      if (value === null || value === undefined || value === '') {
        return '0';
      }
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? '0' : parsed.toString();
    },
    calculateTotalQuantity() {
      if (!this.facility_request?.ecosystem_products || this.facility_request.ecosystem_products.length === 0) {
        return "N/A";
      }
      const totalQuantity = this.facility_request.ecosystem_products.reduce((sum, product) => {
        const quantity = parseInt(product.requested_quantity, 10);
        return sum + (isNaN(quantity) ? 0 : quantity);
      }, 0);
      return totalQuantity;
    },
    getPendingQuantity(selectedProduct) {
      const pending_quantity = selectedProduct.requested_quantity - selectedProduct.issued_quantity;
      return pending_quantity;
    },
    printIssueout(selectedProduct) {
      const requestData = JSON.parse(JSON.stringify(selectedProduct));
      const component = JSON.parse(JSON.stringify(this.selectedComponent));
      const printWindow = document.createElement('iframe');
      printWindow.style.position = 'absolute';
      printWindow.style.width = '0';
      printWindow.style.height = '0';
      printWindow.style.border = 'none';
      document.body.appendChild(printWindow);

      try {
        const printContent = `
          <div class="page-container">
            <div class="report-container">
              <div class="logo-container">
                <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
              </div>
              <h1 class="text-lg font-bold mb-4">Blood Request Issueouts</h1>
              <div class="section">
                <div class="section-title">Request Information</div>
                <div class="grid">
                  <div>
                    <div class="info-row"><span class="info-label">Date Required:</span> ${this.formatDateISO(this.facility_request.date_required)}</div>
                    <div class="info-row"><span class="info-label">Requesting Facility:</span> ${requestData.facility_issued_to?.name || 'N/A'}</div>
                    <div class="info-row"><span class="info-label">Component requested:</span> ${component.unit_product_description || 'N/A'}</div>
                    <div class="info-row"><span class="info-label">Component quantity:</span> ${component.requested_quantity || 'N/A'}</div>
                  </div>
                  <div>
                    <div class="info-row"><span class="info-label">Requested On:</span> ${this.formatDateISO(this.facility_request.created_at)}</div>
                    <div class="info-row"><span class="info-label">Request To:</span> ${requestData.facility_issuing?.name || 'N/A'}</div>
                    <div class="info-row"><span class="info-label">Status:</span> ${component.status}</div>                    
                  </div>
                </div>
              </div>
              <div class="section">
                <div class="section-title">Units Issued</div>
                <div class="table-container">
                  <table class="w-full">
                    <thead>
                      <tr>
                        <th>Serial No.</th>
                        <th>Blood Group</th>
                        <th>Unit Product</th>
                        <th>Quantity</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${requestData.products
            .map(
              (product, index) => `
                            <tr${index % 2 === 0 ? ' class="bg-gray-50"' : ''}>
                              <td>${product.pint_serial || 'N/A'}</td>
                              <td>${product.blood_group.description}</td>
                              <td>${product.unit_product.description}</td>
                              <td>${product.volume}</td>
                              <td>${product.status}</td>
                            </tr>
                          `
            )
            .join('')}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        `;

        const printDocument = `
          <html>
          <head>
            <title>Blood Request Details</title>
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 2cm;
                }
              }
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                margin: 0;
                padding: 0;
              }
              .page-container {
                padding: 20px;
              }
              .logo-container {
                text-align: center;
                margin: 0 auto;
                display: flex;
                justify-content: center;
                margin-bottom: 15px;
              }
              .logo {
                width: 120px;
                align-items: center;
              }
              .report-container {
                max-width: 100%;
                margin: 0 auto;
              }
              h1 {
                text-align: center;
                font-size: 24px;
                margin-bottom: 5px;
              }
              .section {
                margin-bottom: 30px;
              }
              .section-title {
                font-size: 18px;
                font-weight: bold;
                border-bottom: 1px solid #ddd;
                padding-bottom: 8px;
                margin-bottom: 15px;
              }
              .grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 15px;
              }
              .info-row {
                margin-bottom: 8px;
              }
              .info-label {
                font-weight: bold;
              }
              .table-container {
                width: 100%;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 15px;
                font-size: 12px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px 12px;
                text-align: left;
              }
              th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .bg-gray-50 {
                background-color: #f9f9f9;
              }
              @media print {
                body {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `;

        printWindow.contentDocument.open();
        printWindow.contentDocument.write(printDocument);
        printWindow.contentDocument.close();

        printWindow.contentWindow.onload = function () {
          printWindow.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        };
      } catch (error) {
        console.error('Error generating print content:', error);
        document.body.removeChild(printWindow);
      }
    },
  },
};
</script>